/**
 * What a merchant logo is allowed to be — the ONE place the rules live.
 *
 * Lifted out of `MerchantLogoField.tsx` for #468, which adds a second picker
 * (the create form, which has no merchant id yet and so cannot upload at pick
 * time). Two pickers restating "JPEG, PNG or WebP, under 1 MB" is exactly how
 * the two drift, and the copy a user reads is a promise about the SERVER's
 * behaviour — it has to come from one place or one of the two becomes a lie.
 */

/**
 * Accepted by `upload_merchant_logo`'s own description — "Accepts JPEG, PNG,
 * or WebP images." Checked against the FILE's own `type`, not its extension:
 * a renamed `.png` still reports its real MIME type from the OS, where an
 * extension is just a string a user typed.
 */
export const ACCEPTED_TYPES = ['image/jpeg', 'image/png', 'image/webp']

/**
 * 1 MB, not the original brief's 2 MB.
 *
 * That brief's premise — "the API documents no limit" — does not hold against
 * `openapi.json`: `upload_merchant_logo`'s description says "max 1MB raw" and
 * `Body_upload_merchant_logo_…`'s own `logo` field repeats it. Rejecting at
 * the DOCUMENTED ceiling means the message the picker shows is a promise the
 * server actually keeps, rather than a client guess that lets a 3 MB file
 * through to a 413 the user has already been told will not happen.
 */
export const MAX_LOGO_BYTES = 1 * 1024 * 1024

function formatMb(bytes: number): string {
  return `${Math.round(bytes / (1024 * 1024))} MB`
}

/**
 * The message to show, or `undefined` when the file is acceptable.
 *
 * **Neither check is a substitute for the server's own.** A renamed `.png`
 * that is actually a PDF still reaches `upload_merchant_logo`, which is the
 * real authority. These exist so a user who picked a spreadsheet or a 40 MB
 * photo learns why without a round trip — and, on the create path, so a file
 * that could never be accepted is refused BEFORE a merchant is created for it.
 */
export function validateLogoFile(file: File): string | undefined {
  if (!ACCEPTED_TYPES.includes(file.type)) return 'Choose a JPEG, PNG or WebP image.'
  if (file.size > MAX_LOGO_BYTES) return `Images must be under ${formatMb(MAX_LOGO_BYTES)}.`
  return undefined
}
